"use client";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";
import { ParentProfileSchema } from "src/mocks";
import FormOne from "./form-one";
import FormTwo from "./form-two";
import FormThree from "./form-three";
import FormFour from "./form-four";
import FormFive from "./form-five";
import { NunitoFont } from "@/src/fonts/fonts-config";
import { SideBar, Navbar } from "@/src/components";

const ParentProfileForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [utilityBillUrl, setUtilityBillUrl] = useState<string>("");
  const [currentMaritalStatus, setCurrentMaritalStatus] = useState({
    label: "",
    value: "",
  });
  const [currentGender, setCurrentGender] = useState({ label: "", value: "" });

  const [currentResident, setCurrentResident] = useState({
    label: "",
    value: "",
  });
  const [currentIdentity, setCurrentIdentity] = useState({
    label: "",
    value: "",
  });
  const [currentOccupation, setCurrentOccupation] = useState({
    label: "",
    value: "",
  });
  const [currentIncome, setCurrentIncome] = useState({
    label: "",
    value: "",
  });
  const [currentPayment, setCurrentPayment] = useState({
    label: "",
    value: "",
  });
  const [currentEmployement, setCurrentEmployement] = useState({
    label: "",
    value: "",
  });
  const [currentAccount, setCurrentAccount] = useState({
    label: "",
    value: "",
  });
  const [currentBank, setCurrentBank] = useState({
    label: "",
    value: "",
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    setValue,
    getValues,
    resetField,
    trigger,
    control,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(ParentProfileSchema),
    mode: "onChange",
  });

  useFormPersist("parent-profile", {
    watch,
    setValue,
  });

  useEffect(() => {
    const storedFormValues = JSON.parse(
      sessionStorage.getItem("parent-profile") ?? "{}"
    );

    setCurrentMaritalStatus({
      label: storedFormValues?.maritalStatus?.toUpperCase(),
      value: storedFormValues?.maritalStatus,
    });
    setCurrentGender({
      label: storedFormValues?.gender?.toUpperCase(),
      value: storedFormValues?.gender,
    });
    setCurrentIdentity({
      label: storedFormValues?.proofOfIdentity?.toUpperCase(),
      value: storedFormValues?.proofOfIdentity,
    });
    setCurrentResident({
      label: storedFormValues?.residentialStatus?.toUpperCase(),
      value: storedFormValues?.residentialStatus,
    });
    setCurrentOccupation({
      label: storedFormValues?.occupation?.toUpperCase(),
      value: storedFormValues?.occupation,
    });
    setCurrentIncome({
      label: storedFormValues?.grossAnnualIncome?.toUpperCase(),
      value: storedFormValues?.grossAnnualIncome,
    });
    setCurrentPayment({
      label: storedFormValues?.frequencyOfPayment?.toUpperCase(),
      value: storedFormValues?.frequencyOfPayment,
    });
    setCurrentEmployement({
      label: storedFormValues?.employmentStatus?.toUpperCase(),
      value: storedFormValues?.employmentStatus,
    });
    setCurrentAccount({
      label: storedFormValues?.accountType?.toUpperCase(),
      value: storedFormValues?.accountType,
    });
    setCurrentBank({
      label: storedFormValues?.bankName?.toUpperCase(),
      value: storedFormValues?.bankName,
    });
    setImageUrl(storedFormValues?.proofOfIdentityDocumentURL);
    setUtilityBillUrl(storedFormValues?.utilityBillDocumentURL);
  }, [setValue]);

  const nextStep = () => {
    setCurrentStep((prevStep) => (prevStep < 5 ? prevStep + 1 : prevStep));
  };

  const prevStep = () => {
    setCurrentStep((prevStep) => (prevStep > 1 ? prevStep - 1 : prevStep));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormOne
            getValues={getValues}
            register={register}
            errors={errors}
            isValid={isValid}
            resetField={resetField}
            control={control}
            trigger={trigger}
            nextStep={nextStep}
            setValue={setValue}
            currentIdentity={currentIdentity}
            currentGender={currentGender}
            currentMaritalStatus={currentMaritalStatus}
            currentResident={currentResident}
            setCurrentGender={setCurrentGender}
            setCurrentIdentity={setCurrentIdentity}
            setCurrentMaritalStatus={setCurrentMaritalStatus}
            setCurrentResident={setCurrentResident}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            utilityBillUrl={utilityBillUrl}
            setUtilityBillUrl={setUtilityBillUrl}
          />
        );
      case 2:
        return (
          <FormTwo
            getValues={getValues}
            resetField={resetField}
            errors={errors}
            isValid={isValid}
            control={control}
            trigger={trigger}
            nextStep={nextStep}
            setValue={setValue}
            register={register}
            currentOccupation={currentOccupation}
            setCurrentOccupation={setCurrentOccupation}
            currentIncome={currentIncome}
            setCurrentIncome={setCurrentIncome}
            currentPayment={currentPayment}
            setCurrentPayment={setCurrentPayment}
            currentEmployment={currentEmployement}
            setCurrentEmployment={setCurrentEmployement}
            currentBank={currentBank}
            currentAccount={currentAccount}
            setCurrentBank={setCurrentBank}
            setCurrentAccount={setCurrentAccount}
          />
        );
      case 3:
        return (
          <FormThree
            getValues={getValues}
            register={register}
            errors={errors}
            isValid={isValid}
            resetField={resetField}
            control={control}
            trigger={trigger}
            nextStep={nextStep}
            setValue={setValue}
          />
        );
      case 4:
        return (
          <FormFour
            resetField={resetField}
            nextStep={nextStep}
            getValues={getValues}
            isValid={isValid}
            trigger={trigger}
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
          />
        );
      case 5:
        return (
          <FormFive
            nextStep={nextStep}
            getValues={getValues}
            resetField={resetField}
            isValid={isValid}
            trigger={trigger}
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
          />
        );
      default:
        return <div>Unknown Step</div>;
    }
  };

  return (
    <div
      className={`flex flex-col items-center min-h-screen ${NunitoFont.className}`}
    >
      <div className="w-full max-w-sm bg-[#f5fffb] px-5 has-[*:checked]:overflow-hidden relative h-full flex-1 flex flex-col">
        <Navbar prevStep={prevStep} currentStep={currentStep} />
        <main className="flex-grow">{renderStep()}</main>
        <SideBar />
      </div>
    </div>
  );
};

export default ParentProfileForm;
